"use client";
import { useEffect, useRef, useState } from 'react';

import { useRouter } from 'next/navigation';

import HeaderModalWrapper from '@Components/modals/modal-wrapper/header-modal-wrapper';
import { Button } from '@app/shadcn/components/ui/button';
import { useToast } from '@app/shadcn/components/ui/use-toast';

import { Trans, useTranslation } from 'react-i18next';

import { useModal } from '@app/components/modal-views/context';
import { googleFormsImport } from '@app/constants/locales/google-forms-import';
import { sheetsReconnect } from '@app/constants/locales/sheets-reconnect';
import { configuredDashboardOrigin, oauthMessage } from '@app/lib/google-oauth-popup';
import { googleFormsRefusal, useGoogleFormsImportMode } from '@app/lib/hooks/use-google-forms-import-mode';
import { IntegrationType } from '@app/models/enums/integration-type';
import { AppInput } from '@app/shadcn/components/ui/input';
import { useAddActionToFormMutation } from '@app/store/api-actions-api';
import { selectAuth } from '@app/store/auth/slice';
import { useAppSelector } from '@app/store/hooks';
import { useHandleIntegrationOauthCallbackMutation, useLazyGetIntegrationOauthUrlQuery } from '@app/store/integration-apis';
import { selectWorkspace } from '@app/store/workspaces/slice';
import Image from 'next/image';

export default function AddActionToFormModal({ action, form, reconnect = false, ...props }: any) {
    const { toast } = useToast();
    const { closeModal } = useModal();
    const [addActionToForm, { isLoading }] = useAddActionToFormMutation();
    const [fetchOauthUrl, { data }] = useLazyGetIntegrationOauthUrlQuery();
    const [handleOauthCallback] = useHandleIntegrationOauthCallbackMutation();

    const [parameters, setParameters] = useState<any>({});
    const workspace = useAppSelector(selectWorkspace);
    const router = useRouter();
    const [error, setError] = useState(false);

    const user = useAppSelector(selectAuth);
    const [showIntegrationConsent, setShowIntegrationConsent] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [oauthResult, setOauthResult] = useState({ state: '', code: '' });
    // the consent popup this modal opened: the only window it takes a result from
    const popupRef = useRef<Window | null>(null);
    const { t } = useTranslation();
    // GOOGLE_FORMS_IMPORT=off: the Sheets action still goes through the
    // Google Forms service, so connecting answers 410 google_forms_off.
    const { mode: googleImportMode } = useGoogleFormsImportMode();
    const sheetsOff = googleImportMode === 'off';

    useEffect(() => {
        if (action.name === 'creator_copy_mail') {
            setParameters({ ['Receiving Mail Address']: user.email });
        } else if (action.name === IntegrationType.GoogleSheet) {
            setShowIntegrationConsent(true);
            if (reconnect) {
                // Reconnecting an action already on the form: keep its sheet title.
                const current: Record<string, string> = {};
                form?.parameters?.[action.id]?.forEach((param: any) => {
                    if (action?.parameters?.some((p: any) => p.required && p.name === param.name)) current[param.name] = param.value;
                });
                setParameters(current);
            }
        }
    }, [action]);

    useEffect(() => {
        if (oauthResult.code && oauthResult.state) {
            const body = { ...oauthResult, action_id: action.id, form_id: form.formId };
            const request = {
                body,
                integrationType: IntegrationType.GoogleSheet
            };
            handleOauthCallback(request).then((result: any) => {
                if (result?.data) {
                    setShowIntegrationConsent(false);
                }
            });
        }
    }, [oauthResult.state]);

    // The consent popup's result: only from that popup, on the dashboard origin.
    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            const result = oauthMessage(event, configuredDashboardOrigin(), popupRef.current);
            if (!result) return;
            if (result.error) {
                setErrorMessage(t('STAFF.INTEGRATIONS.ACCESS_DENIED'));
            } else if (result.state && result.code) {
                setOauthResult({ state: result.state, code: result.code });
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, []);

    const handleClick = () => {
        setErrorMessage('');
        fetchOauthUrl({ integrationType: IntegrationType.GoogleSheet }).then((res) => {
            if (googleFormsRefusal(res.error) === 'off') {
                setErrorMessage(t(googleFormsImport.off.sheets));
                return;
            }
            if (!res.data) {
                setErrorMessage(t('STAFF.INTEGRATIONS.GOOGLE_UNREACHABLE'));
                return;
            }
            popupRef.current = window.open(res.data, 'oauthWindow', 'height=600 width=600 left=600 top=300');
        });
    };

    if (showIntegrationConsent) {
        return (
            <HeaderModalWrapper headerTitle={reconnect ? t(sheetsReconnect.button) : t('STAFF.INTEGRATIONS.ADD_SHEETS_HEADER')}>
                <div className="relative flex flex-col rounded p-8 md:min-w-[400px]">
                    <div className="mb-6 flex justify-center">
                        <Image src={action?.url || '/integration/default.svg'} alt={t('STAFF.INTEGRATIONS.SHEETS_ICON')} width={80} height={80} />
                    </div>
                    <div className="mb-4 text-center text-2xl font-bold text-gray-800">
                        <Trans i18nKey="STAFF.INTEGRATIONS.TO_THE_FORM" values={{ title: action?.title || t('STAFF.COMMON.UNTITLED'), form: form?.title || t('STAFF.COMMON.UNTITLED') }} components={{ 1: <span className="text-blue-600" /> }} />
                    </div>
                    <p className="text-center text-sm text-gray-600 ">{t('STAFF.INTEGRATIONS.SHEETS_SEND')}</p>
                    {sheetsOff ? (
                        <p role="note" className="mt-4 max-w-[400px] rounded-lg border border-black-300 bg-black-100 px-4 py-3 text-sm text-black-800">
                            {t(googleFormsImport.off.sheets)}
                        </p>
                    ) : (
                        <Button className="mt-4" onClick={handleClick}>
                            Connect to Google{' '}
                        </Button>
                    )}
                    {errorMessage && <p role="alert" className={'mt-4 max-w-[400px] text-sm text-red-500'}>{errorMessage}</p>}
                </div>
            </HeaderModalWrapper>
        );
    }

    const onAddIntegration = async () => {
        let error = false;
        action?.parameters?.forEach((parameter: any) => {
            if (parameter.required && !parameters[parameter?.name]) {
                error = true;
            }
        });

        const overridingParams: Array<any> = [];
        Object.keys(parameters).map((paramName: string) => {
            overridingParams.push({
                name: paramName,
                value: parameters[paramName]
            });
        });

        const response: any = await addActionToForm({
            workspaceId: workspace.id,
            formId: form.formId,
            body: {
                action_id: action.id,
                trigger: 'on_submit',
                parameters: overridingParams
            }
        });
        if (response?.data) {
            router.refresh();
            toast({ description: 'Added' });
            closeModal();
        } else if (response?.error) {
            toast({ description: 'Error', variant: 'destructive' });
        }
        setError(error);
    };

    return (
        <HeaderModalWrapper headerTitle={t('STAFF.INTEGRATIONS.ADD_HEADER')}>
            {/* This is the front popup */}
            <div className="relative flex flex-col rounded  p-8  md:min-w-[400px]">
                {/* Top Banner/Icon Section with Illustrations */}
                <div className="mb-6 flex justify-center">
                    <Image src={action?.url || '/integration/default.svg'} alt={t('STAFF.INTEGRATIONS.ICON')} width={80} height={80} />
                </div>

                {/* Title with Enhanced Typography */}
                <div className="mb-4 text-center text-2xl font-bold text-gray-800">
                    <Trans i18nKey="STAFF.INTEGRATIONS.ADDING_TO_FORM" values={{ title: action?.title || t('STAFF.COMMON.UNTITLED'), form: form?.title || t('STAFF.COMMON.UNTITLED') }} components={{ 1: <span className="text-blue-600" /> }} />
                </div>

                {/* Floating Label Input Fields */}
                {action?.parameters?.filter((param: any) => param.required).length > 0 && (
                    <>
                        <div className="mb-2 text-lg font-semibold text-gray-700">{t('STAFF.INTEGRATIONS.REQUIRED_PARAMS')}</div>
                        <div className="space-y-4">
                            {action?.parameters?.map((parameter: any, index: number) =>
                                parameter?.required ? (
                                    <div key={index} className="relative">
                                        <AppInput
                                            className="w-full"
                                            placeholder={parameter.name}
                                            value={parameters[parameter.name]}
                                            onChange={(event) => {
                                                setParameters({ ...parameters, [parameter.name]: event.target.value });
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div key={index}></div>
                                )
                            )}
                        </div>
                        {error && <div className="mt-2 text-sm text-red-500">{t('STAFF.INTEGRATIONS.FILL_PARAMS')}</div>}
                    </>
                )}

                {/* Add Integration Button */}
                <Button data-umami-event={`Add ${action?.title} Integration`} variant="primary" size="medium" onClick={onAddIntegration} className="mt-4">
                    {t('STAFF.INTEGRATIONS.ADD_BUTTON')}
                </Button>
            </div>
        </HeaderModalWrapper>
    );
}
